import React, { useState, useEffect } from 'react';
import { Heart, Users, Calendar, Search, Plus, Edit2, Trash2, Phone, Mail, MapPin, AlertCircle, X } from 'lucide-react';
import NewAgenziaAmoreClientModal from '../components/NewAgenziaAmoreClientModal';
import { agenziaAmoreApi, contactApi } from '../lib/api';

interface Client {
  id: string;
  contact_id: string;
  name: string;
  email: string;
  phone: string;
  age: number;
  gender: string;
  city: string;
  preferences: string;
  status: 'active' | 'matched' | 'paused';
  notes: string;
  created_at: string;
}

const AgenziaAmore = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; client: Client | null }>({
    isOpen: false,
    client: null
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await agenziaAmoreApi.getAll();
      const formattedClients = data.map((item: any) => {
        const age = item.contact?.birthdate ? calculateAge(item.contact.birthdate) : 0;
        return {
          id: item.id,
          contact_id: item.contact_id,
          name: item.contact?.name || '',
          email: item.contact?.email || '',
          phone: item.contact?.phone || '',
          age: age,
          gender: getGenderShortLabel(item.contact?.gender || ''),
          city: item.contact?.city || '',
          preferences: item.preferences || '',
          status: item.status,
          notes: item.notes || '',
          created_at: item.created_at
        };
      });
      setClients(formattedClients);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthdate: string) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getGenderShortLabel = (gender: string) => {
    switch (gender) {
      case 'uomo_etero':
      case 'uomo_gay':
        return 'M';
      case 'donna_etero':
      case 'donna_gay':
        return 'F';
      default:
        return 'Altro';
    }
  };

  const handleNewClient = async (clientData: {
    name: string;
    birthdate: string;
    gender: string;
    city: string;
    email: string;
    phone: string;
    status: 'active' | 'inactive' | 'matched';
    preferences: string;
    notes?: string;
    contact_id?: string;
  }) => {
    try {
      setLoading(true);

      let contactId = clientData.contact_id;

      if (!contactId) {
        const newContact = await contactApi.create({
          name: clientData.name,
          email: clientData.email,
          phone: clientData.phone,
          gender: clientData.gender,
          birthdate: clientData.birthdate,
          city: clientData.city,
          notes: clientData.notes || ''
        });

        contactId = newContact.id;
      }

      await agenziaAmoreApi.create({
        contact_id: contactId,
        status: clientData.status === 'inactive' ? 'paused' : clientData.status,
        preferences: clientData.preferences,
        notes: clientData.notes || ''
      });

      await loadClients();
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Errore durante la creazione del cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteConfirmation.client) return;

    try {
      setLoading(true);
      await agenziaAmoreApi.delete(deleteConfirmation.client.id);
      await loadClients();
      setDeleteConfirmation({ isOpen: false, client: null });
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Errore durante l\'eliminazione del cliente');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'matched':
        return 'bg-pink-100 text-pink-800';
      case 'paused':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Attivo';
      case 'matched':
        return 'Abbinato';
      case 'paused':
        return 'In Pausa';
      default:
        return status;
    }
  };

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white">Agenzia Amore</h1>
            <p className="text-white/80 mt-1">Gestisci i clienti dell'agenzia matrimoniale</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Clienti Totali</p>
                  <p className="text-3xl font-bold text-white mt-1">{clients.length}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Clienti Attivi</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {clients.filter(c => c.status === 'active').length}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Match Realizzati</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {clients.filter(c => c.status === 'matched').length}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Heart className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Appuntamenti Mese</p>
                  <p className="text-3xl font-bold text-white mt-1">0</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Cerca per nome o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            >
              <option value="all">Tutti gli Stati</option>
              <option value="active">Attivi</option>
              <option value="matched">Abbinati</option>
              <option value="paused">In Pausa</option>
            </select>
            <button
              onClick={() => setIsNewClientModalOpen(true)}
              className="px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors flex items-center gap-2 font-medium shadow-sm whitespace-nowrap"
            >
              <Plus className="h-5 w-5" />
              Nuovo Cliente
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessun Cliente Trovato
              </h3>
              <p className="text-gray-600 mb-6">
                {clients.length === 0
                  ? "Inizia aggiungendo il tuo primo cliente dell'agenzia"
                  : "Nessun cliente corrisponde ai criteri di ricerca"}
              </p>
              {clients.length === 0 && (
                <button
                  onClick={() => setIsNewClientModalOpen(true)}
                  className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  Aggiungi Primo Cliente
                </button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Cliente</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Contatti</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Età/Genere</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Città</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Stato</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Registrazione</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{client.name}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Mail className="h-3 w-3" />
                          {client.email}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone className="h-3 w-3" />
                          {client.phone}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {client.age} anni - {client.gender}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="h-3 w-3" />
                        {client.city}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                        {getStatusLabel(client.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(client.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingClient(client);
                            setIsEditModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                          title="Modifica"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmation({ isOpen: true, client })}
                          className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                          title="Rimuovi da Agenzia Amore"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <NewAgenziaAmoreClientModal
        isOpen={isNewClientModalOpen}
        onClose={() => setIsNewClientModalOpen(false)}
        onSubmit={handleNewClient}
      />

      {deleteConfirmation.isOpen && deleteConfirmation.client && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="bg-red-100 rounded-full p-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Rimuovere Cliente da Agenzia Amore?
                </h3>
                <p className="text-gray-600 mb-4">
                  Sei sicuro di voler rimuovere <strong>{deleteConfirmation.client.name}</strong> dalla lista dell'Agenzia Amore?
                  Il contatto rimarrà comunque salvato nella lista partecipanti.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirmation({ isOpen: false, client: null })}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleDeleteClient}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {loading ? 'Rimozione...' : 'Rimuovi'}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setDeleteConfirmation({ isOpen: false, client: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editingClient && (
        <EditClientModal
          client={editingClient}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingClient(null);
          }}
          onSave={async (updatedData) => {
            try {
              setLoading(true);
              await agenziaAmoreApi.update(editingClient.id, updatedData);
              await loadClients();
              setIsEditModalOpen(false);
              setEditingClient(null);
            } catch (error) {
              console.error('Error updating client:', error);
              alert('Errore durante l\'aggiornamento del cliente');
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
    </div>
  );
};

interface EditClientModalProps {
  client: Client;
  onClose: () => void;
  onSave: (data: { status: string; preferences: string; notes: string }) => void;
}

const EditClientModal: React.FC<EditClientModalProps> = ({ client, onClose, onSave }) => {
  const [status, setStatus] = useState(client.status);
  const [preferences, setPreferences] = useState(client.preferences);
  const [notes, setNotes] = useState(client.notes);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ status, preferences, notes });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Modifica Cliente</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Informazioni Cliente</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Nome:</span>
                <span className="ml-2 font-medium">{client.name}</span>
              </div>
              <div>
                <span className="text-gray-600">Email:</span>
                <span className="ml-2 font-medium">{client.email}</span>
              </div>
              <div>
                <span className="text-gray-600">Telefono:</span>
                <span className="ml-2 font-medium">{client.phone}</span>
              </div>
              <div>
                <span className="text-gray-600">Città:</span>
                <span className="ml-2 font-medium">{client.city}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stato
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              required
            >
              <option value="active">Attivo</option>
              <option value="matched">Abbinato</option>
              <option value="paused">In Pausa</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferenze Partner
            </label>
            <textarea
              value={preferences}
              onChange={(e) => setPreferences(e.target.value)}
              placeholder="Descrivi le preferenze del partner ideale..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note aggiuntive..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium"
            >
              Salva Modifiche
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgenziaAmore;
